import type { TFunction } from 'i18next';
import type {
  AreaDecisionType,
  TeamPage,
  TeamStep,
} from '../../api/workEnvironmentTypes';
import { joinList } from '../HRDashboard/hmsFormat';
import { longDate } from '../Settings/privacyView';
import { monthAndYear } from '../WorkEnvironmentSurveyDetail/evaluationView';
import { dayAndMonth } from '../WorkEnvironmentSurveyDetail/surveyProcessView';

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

/** «21.–28. september», or both months when they differ */
const dayRange = (from: string, to: string, language: string) => {
  const [start, end] = [new Date(from), new Date(to)];
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  const startText = sameMonth
    ? new Intl.DateTimeFormat(locale(language), { day: 'numeric' }).format(
        start,
      )
    : `${dayAndMonth(from, language)} `;
  return `${startText}–${sameMonth ? '' : ' '}${dayAndMonth(to, language)}`;
};

const DETAILS: Record<
  TeamStep,
  (data: TeamPage, t: TFunction, language: string) => string | null
> = {
  survey: ({ survey }, t, language) =>
    t('workEnvironmentTeamPage.steps.surveyDetail', {
      responses: survey.responses,
      expected: survey.expected,
      period: dayRange(survey.from, survey.to, language),
    }),
  assessed: ({ assessment }, _t, language) =>
    assessment ? dayAndMonth(assessment.at, language) : null,
  proposals: ({ proposals }, t) =>
    t('workEnvironmentTeamPage.steps.proposalsDetail', {
      count: proposals.count,
      voters: proposals.voters,
      expected: proposals.expected,
    }),
  plan: ({ plans }, t, language) =>
    plans[0]
      ? t('workEnvironmentTeamPage.steps.planDetail', {
          date: dayAndMonth(plans[0].approvedAt, language),
        })
      : null,
  followUp: () => null,
  evaluation: ({ evaluationDate }, _t, language) =>
    evaluationDate ? longDate(evaluationDate, language) : null,
};

/** What happened in a step that is done, or «Nå» for the step the team is at */
export const stepDetail = (
  step: TeamStep,
  data: TeamPage,
  t: TFunction,
  language: string,
) => DETAILS[step](data, t, language);

/** «Teamet jobber med tiltaksplanen. Effekten måles med en ny undersøkelse i april 2027.» */
export const whereWeAre = (data: TeamPage, t: TFunction, language: string) => {
  const current =
    data.steps.find((step) => step.state === 'current')?.step ?? 'done';
  const now = t(`workEnvironmentTeamPage.now.${current === 'plan' && !data.safetyRep ? 'planNoRep' : current}`);
  return data.evaluationDate && current !== 'evaluation' && current !== 'done'
    ? `${now} ${t('workEnvironmentTeamPage.now.measured', { month: monthAndYear(data.evaluationDate, language) })}`
    : now;
};

export const measuresDone = (plan: TeamPage['plans'][number]) =>
  plan.measures.filter((measure) => measure.status === 'DONE').length;

/** Without a safety representative, the team leader gave the reason alone */
export const decisionText = (
  area: { decision: AreaDecisionType; deadline: string | null },
  data: Pick<TeamPage, 'safetyRep'>,
  t: TFunction,
  language: string,
) => {
  if (area.decision === 'PLAN_LATER' && area.deadline)
    return t('workEnvironmentTeamPage.decisions.PLAN_LATER_BY', { date: dayAndMonth(area.deadline, language) });
  const decision = area.decision === 'NOT_NOW' && !data.safetyRep ? 'NOT_NOW_NO_REP' : area.decision;
  return t(`workEnvironmentTeamPage.decisions.${decision}`);
};

/**
 * Why the sensitive areas are shielded, and where to speak up. Without a whistleblowing link,
 * the organisation's own routines. Without a safety representative, HR.
 */
export const shieldedTexts = (
  data: Pick<TeamPage, 'safetyRep' | 'whistleblowingUrl'>,
  areas: string,
  t: TFunction,
) => {
  const noRep = data.safetyRep ? '' : 'NoRep';
  return {
    why: t(`workEnvironmentTeamPage.result.why${noRep}`, { areas }),
    report: t(`workEnvironmentTeamPage.result.report${data.whistleblowingUrl ? '' : 'NoLink'}${noRep}`),
  };
};

/** «Trakassering og vold og trusler» */
export const shieldedNames = (areas: TeamPage['areas'], t: TFunction) =>
  joinList(
    areas
      .filter((area) => area.level === null)
      .map((area, index) =>
        index === 0 ? area.name : area.name.toLowerCase(),
      ),
    t,
    'and',
  );
